import { useEffect, useState } from 'react';
import { getSDK } from '@/store/sdkStore';
import { registerContentHandler, type ContentViewProps } from '@/lib/contentRegistry';
import { bytesToBlob } from '@/lib/fileBytes';
import { MediaPlayer } from '@/components/media/MediaPlayer';

// ============================================================================
// builtinContentHandlers.tsx — ВСТРОЕННЫЕ обработчики контента (SSoT).
// Регистрируются при первом использовании (ensureBuiltinContentHandlers).
// Приложения могут ПЕРЕОПРЕДЕЛИТЬ (upsertContentHandler / register позже):
// реестр отдаёт последнего. Расширяемо — любые форматы.
// ============================================================================

// useFileObjectUrl — загрузка файла (DownloadFile) → object URL.
function useFileObjectUrl(fileId: string): { url: string; error?: string } {
  const [state, setState] = useState<{ url: string; error?: string }>({ url: '' });
  useEffect(() => {
    let alive = true;
    let objectUrl = '';
    (async () => {
      try {
        const r: any = await getSDK().files.downloadFile({ id: fileId });
        const blob = bytesToBlob(r?.data ?? r?.content);
        objectUrl = URL.createObjectURL(blob);
        if (alive) setState({ url: objectUrl });
      } catch (e: any) {
        if (alive) setState({ url: '', error: e?.message || 'ошибка загрузки' });
      }
    })();
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [fileId]);
  return state;
}

// useParsedContent — ParseContent (серверный разбор xlsx/csv/docx/text).
function useParsedContent(fileId: string): { parsed?: any; error?: string } {
  const [state, setState] = useState<{ parsed?: any; error?: string }>({});
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r: any = await getSDK().files.parseContent({ fileId });
        if (r?.error) { if (alive) setState({ error: r.error }); return; }
        if (alive) setState({ parsed: r });
      } catch (e: any) {
        if (alive) setState({ error: e?.message || 'ошибка разбора' });
      }
    })();
    return () => { alive = false; };
  }, [fileId]);
  return state;
}

// ---- Изображения / PDF / аудио / видео: blob URL ----
function MediaView({ fileId, fileName, fileType }: ContentViewProps) {
  const { url, error } = useFileObjectUrl(fileId);
  const ext = (fileName.split('.').pop() || '').toLowerCase();
  if (error) return <div className="p-2 text-sm text-destructive">{error}</div>;
  if (!url) return <div className="p-2 text-sm text-muted-foreground">Загрузка…</div>;
  const mime = fileType || (ext === 'pdf' ? 'application/pdf' : `image/${ext === 'jpg' ? 'jpeg' : ext}`);
  if (ext === 'pdf') {
    return <iframe src={url} title={fileName} className="h-full w-full rounded border" />;
  }
  return <img src={url} alt={fileName} className="max-h-full max-w-full rounded border object-contain" />;
}

// ---- xlsx / csv: серверный ParseContent → грид ----
function GridView({ fileId, fileName }: ContentViewProps) {
  const { parsed, error } = useParsedContent(fileId);
  if (error) return <div className="p-2 text-sm text-destructive">{error}</div>;
  if (!parsed) return <div className="p-2 text-sm text-muted-foreground">Загрузка…</div>;
  const sheets = parsed?.sheets || [];
  if (!sheets.length) return <div className="p-2 text-sm text-muted-foreground">Нет данных</div>;
  return (
    <div className="h-full space-y-2 overflow-auto">
      {sheets.map((sheet: any, si: number) => (
        <div key={si} className="rounded border">
          <div className="border-b bg-muted px-2 py-1 text-xs font-medium">{sheet?.name || `Лист ${si + 1}`}</div>
          <div className="overflow-auto">
            <table className="w-full border-collapse text-xs">
              <tbody>
                {(sheet?.rows || []).map((row: string[], ri: number) => (
                  <tr key={ri} className="border-b last:border-0">
                    {row.map((cell, ci) => (
                      <td key={ci} className={`border-r px-2 py-0.5 last:border-0 ${ri === 0 ? 'bg-muted/60 font-medium' : ''}`}>
                        {cell ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {fileName.endsWith('.xlsx') && <div className="text-[10px] text-muted-foreground">Просмотр до 50 строк на лист (полный файл — скачайте).</div>}
    </div>
  );
}

// ---- docx / text / md / json: серверный ParseContent → текст ----
function TextView({ fileId, fileName }: ContentViewProps) {
  const { parsed, error } = useParsedContent(fileId);
  if (error) return <div className="p-2 text-sm text-destructive">{error}</div>;
  if (!parsed) return <div className="p-2 text-sm text-muted-foreground">Загрузка…</div>;
  return <pre className="h-full overflow-auto whitespace-pre-wrap rounded border bg-muted/40 p-3 text-xs">{parsed?.text || '(пусто)'}</pre>;
}

// ---- Аудио / видео: ЕДИНЫЙ SSoT проигрыватель (тот же, что в чате) ----
function AudioView({ fileId, fileName }: ContentViewProps) {
  return (
    <div className="p-3">
      <MediaPlayer fileId={fileId} kind="audio" name={fileName} />
    </div>
  );
}

function VideoView({ fileId, fileName }: ContentViewProps) {
  return (
    <div className="flex h-full items-center justify-center p-2">
      <MediaPlayer fileId={fileId} kind="video" name={fileName} />
    </div>
  );
}

// ---- Регистрация встроенных (при первом вызове) ----
let ensured = false;
export function ensureBuiltinContentHandlers(): void {
  if (ensured) return;
  ensured = true;
  registerContentHandler({ id: 'builtin.image', label: 'Просмотр', extensions: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico', 'avif'], view: MediaView });
  registerContentHandler({ id: 'builtin.pdf', label: 'PDF', extensions: ['pdf'], view: MediaView });
  registerContentHandler({ id: 'builtin.xlsx', label: 'Таблица (грид)', extensions: ['xlsx', 'xlsm', 'csv', 'tsv'], view: GridView });
  registerContentHandler({ id: 'builtin.docx', label: 'Текст (docx)', extensions: ['docx'], view: TextView });
  registerContentHandler({ id: 'builtin.text', label: 'Текст', extensions: ['txt', 'md', 'markdown', 'log', 'json', 'xml', 'html', 'css', 'js', 'ts', 'go', 'sql', 'yaml', 'ini', 'cfg'], view: TextView });
  // Аудио/видео (после текстовых: webm зарегистрирован в обоих — побеждает
  // видео-плеер, который корректно играет и аудио-only webm; в ЧАТЕ разбор
  // точнее — по MIME file_type, см. mediaKind).
  registerContentHandler({ id: 'builtin.audio', label: 'Аудио', extensions: ['mp3', 'wav', 'ogg', 'opus', 'm4a', 'aac', 'flac'], view: AudioView });
  registerContentHandler({ id: 'builtin.video', label: 'Видео', extensions: ['mp4', 'webm', 'mov', 'mkv', 'avi', 'm4v'], view: VideoView });
}
